/* Cart drawer extras: one-tap "Pair it up" adds, and a burst of confetti when a discount code is accepted or
   a spend goal (free shipping, ...) is reached.
   Applying and removing codes, quantities and the drawer itself are still handled by the theme's cart script;
   this file only listens to what that script reports. */
(() => {
  if (window.rookCartReady) return;
  window.rookCartReady = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const drawer = () => document.querySelector('rook-cart-drawer');

  /* ---- Pair it up: add one piece without leaving the drawer ---- */
  document.addEventListener('click', (event) => {
    const button = event.target.closest ? event.target.closest('[data-rook-cart-add]') : null;
    if (!button || button.disabled) return;
    event.preventDefault();
    const routes = (window.RookSettings && window.RookSettings.routes) || {};
    const addUrl = (routes.cart_add_url || '/cart/add') + '.js';
    const label = button.innerHTML;
    button.disabled = true;
    button.classList.add('is-busy');
    fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(button.dataset.rookCartAdd), quantity: 1 }] }),
    })
      .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) throw new Error(data.description || data.message || 'Could not add this piece');
        // the theme re-renders the drawer (and the cart count) from the server on this event
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
      })
      .catch((problem) => {
        button.disabled = false;
        button.classList.remove('is-busy');
        button.innerHTML = label;
        button.setAttribute('title', problem.message);
        button.classList.add('is-failed');
        window.setTimeout(() => button.classList.remove('is-failed'), 2400);
      });
  });

  /* ---- Confetti when a discount code is accepted ---- */
  const applied = (cart) => ((cart && cart.discount_codes) || []).filter((entry) => entry.applicable).map((entry) => String(entry.code).toUpperCase());
  // codes already on the cart when the page loads are not news
  let known = new Set(Array.from(document.querySelectorAll('.rook-cart__discount--item[data-discount-code]')).map((item) => item.dataset.discountCode.toUpperCase()));

  // Paper in black, greys and silver. Two bursts from the sides, then a slow fall from the top, so the drawer
  // stays full for a few seconds. Movement is measured in time, not frames: on a fast screen it would otherwise
  // run at double speed and be gone at once.
  const confetti = () => {
    const host = drawer();
    const box = host && host.querySelector('.rook-cart-drawer__inner');
    if (!box || reduceMotion) return;
    const earlier = box.querySelector('.rook-confetti');
    if (earlier) earlier.remove();
    const canvas = document.createElement('canvas');
    canvas.className = 'rook-confetti';
    canvas.setAttribute('aria-hidden', 'true');
    box.appendChild(canvas);
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    const width = box.clientWidth;
    const height = box.clientHeight;
    canvas.width = width * ratio;
    canvas.height = height * ratio;
    const ctx = canvas.getContext('2d');
    ctx.scale(ratio, ratio);

    // each piece has a face and a lighter back, shown in turn as it tumbles: that reads as a metal sheen
    const tones = [
      ['#0c0c0d', '#3b3b3f'],
      ['#2c2c2f', '#5d5d62'],
      ['#55555a', '#8a8a90'],
      ['#7f7f85', '#b3b3b8'],
      ['#a6a6ac', '#d6d6da'],
      ['#c4c4c9', '#efeff1'],
    ];
    const piece = (index, start) => ({
      ...start,
      w: 6 + Math.random() * 7,
      h: 9 + Math.random() * 10,
      turn: Math.random() * Math.PI * 2,
      spin: (Math.random() - 0.5) * 0.34,
      flip: Math.random() * Math.PI * 2,
      flipSpeed: 0.08 + Math.random() * 0.14,
      sway: 0.4 + Math.random() * 0.9,
      phase: Math.random() * Math.PI * 2,
      fall: 1.5 + Math.random() * 1.9, // the speed it settles to once the burst is spent
      tone: tones[index % tones.length],
      round: index % 6 === 0,
    });
    const pieces = [];
    // two bursts from each side
    [0, 420].forEach((delay, wave) => {
      for (let index = 0; index < 90; index++) {
        const fromLeft = index % 2 === 0;
        const angle = (fromLeft ? -Math.PI / 3.2 : -Math.PI + Math.PI / 3.2) + (Math.random() - 0.5) * 1.1;
        const speed = 6 + Math.random() * 12;
        pieces.push(piece(index + wave, { delay, x: fromLeft ? -12 : width + 12, y: height * (0.32 + Math.random() * 0.3), vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed }));
      }
    });
    // and a slow fall from the top that keeps coming for a while
    for (let index = 0; index < 170; index++) {
      pieces.push(piece(index + 3, { delay: 150 + Math.random() * 2600, x: Math.random() * width, y: -20 - Math.random() * 60, vx: (Math.random() - 0.5) * 1.6, vy: 0.6 + Math.random() * 1.4 }));
    }

    const started = performance.now();
    const life = 6400;
    let last = started;
    const frame = (now) => {
      const age = now - started;
      const step = Math.min(3, (now - last) / 16.667); // 1 = one frame at 60 per second
      last = now;
      const fade = Math.max(0, 1 - Math.max(0, age - life * 0.72) / (life * 0.28));
      ctx.clearRect(0, 0, width, height);
      pieces.forEach((p) => {
        if (age < p.delay) return;
        p.vy = Math.min(p.fall, p.vy + 0.16 * step); // gravity, up to the settling speed
        p.vx *= Math.pow(0.975, step);
        p.x += (p.vx + Math.sin(age / 420 + p.phase) * p.sway) * step;
        p.y += p.vy * step;
        p.turn += p.spin * step;
        p.flip += p.flipSpeed * step;
        if (p.y > height + 30) return;
        const face = Math.cos(p.flip);
        ctx.save();
        ctx.globalAlpha = fade;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.turn);
        ctx.fillStyle = face >= 0 ? p.tone[0] : p.tone[1];
        if (p.round) {
          ctx.beginPath();
          ctx.ellipse(0, 0, p.w / 2, Math.max(0.8, (p.w / 2) * Math.abs(face)), 0, 0, Math.PI * 2);
          ctx.fill();
        } else {
          const tall = Math.max(1.2, p.h * Math.abs(face));
          ctx.fillRect(-p.w / 2, -tall / 2, p.w, tall);
        }
        ctx.restore();
      });
      if (age < life && canvas.isConnected) requestAnimationFrame(frame);
      else canvas.remove();
    };
    requestAnimationFrame(frame);
  };

  // an amount in the store's own money format ("Rs. {{amount}}")
  const money = (cents) => {
    const format = (window.RookSettings && window.RookSettings.money_format) || 'Rs. {{amount}}';
    const whole = /no_decimals/.test(format);
    const amount = (cents / 100).toLocaleString('en-IN', { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: whole ? 0 : 2 });
    return format.replace(/\{\{\s*amount[a-z_]*\s*\}\}/, amount).replace(/<[^>]*>/g, '');
  };

  // a short note over the drawer: plain text, with an optional part in bold at the end
  const cheer = (text, strong) => {
    const host = drawer();
    if (!host) return;
    const old = host.querySelector('.rook-cart-cheer');
    if (old) old.remove();
    const note = document.createElement('p');
    note.className = 'rook-cart-cheer';
    note.setAttribute('role', 'status');
    note.textContent = text;
    if (strong) {
      const bold = document.createElement('strong');
      bold.textContent = strong;
      note.appendChild(bold);
    }
    const inner = host.querySelector('.rook-cart-drawer__inner');
    if (inner) inner.appendChild(note);
    window.setTimeout(() => note.classList.add('is-leaving'), 5200);
    window.setTimeout(() => note.remove(), 5800);
  };
  const celebrate = (text, strong) => {
    cheer(text, strong);
    const host = drawer();
    if (!host || host.dataset.rookConfetti !== 'false') confetti();
  };

  // 1. a discount code that the store accepts
  document.addEventListener('cart:updated', (event) => {
    const cart = event.detail && event.detail.cart;
    if (!cart || !Array.isArray(cart.discount_codes)) return;
    const now = applied(cart);
    const fresh = now.filter((code) => !known.has(code));
    known = new Set(now);
    if (!fresh.length) return;
    const saved = cart.total_discount > 0 ? money(cart.total_discount) : '';
    celebrate(saved ? `${fresh[0]} applied. You saved ` : `${fresh[0]} applied.`, saved);
  });

  // 2. a spend goal that has just been reached (free shipping, ...). The theme redraws the drawer's header from
  //    the server after every cart change, so the bar there is compared before and after each redraw.
  const reachedNow = () => {
    const bar = document.querySelector('rook-cart-drawer [data-rook-goal]');
    return bar ? Number(bar.dataset.reached) || 0 : 0;
  };
  const watchGoals = () => {
    const header = document.getElementById('RookCartDrawerHeader');
    if (!header || !window.MutationObserver) return;
    let reached = reachedNow(); // goals already reached when the page opens are not news
    new MutationObserver(() => {
      const now = reachedNow();
      if (now > reached) {
        const labels = header.querySelectorAll('.rook-goal__stop.is-reached .rook-goal__label');
        const label = labels.length ? labels[labels.length - 1].textContent.trim() : '';
        if (label) celebrate('', `${label} unlocked`);
      }
      reached = now;
    }).observe(header, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchGoals);
  else watchGoals();

  // for trying the effect out from the browser console: rookCartConfetti()
  window.rookCartConfetti = confetti;
})();
